import { RefreshCw } from 'lucide-react';

import { notifySuccess } from '@/portainer/services/notifications';

import { LoadingButton } from '@@/buttons';

import { useReconcileWorkflow } from './queries/useReconcileWorkflow';
import { Workflow } from './types';

/**
 * Asks a reconciled workflow to apply now rather than at its next interval.
 *
 * Applying is what puts drifted resources back, so this doubles as the manual
 * fix for drift: it re-applies the manifests whatever the deployment's drift
 * mode says about the background pass.
 *
 * Only shown for workflows the reconciliation engine owns. An Edge stack
 * workflow redeploys on its own schedule and has nothing to ask for.
 */
export function ReconcileButton({
  workflow,
  size = 'small',
}: {
  workflow: Workflow;
  /** The list view sits in a denser row than the detail header. */
  size?: 'xsmall' | 'small';
}) {
  const mutation = useReconcileWorkflow();

  const isReconciled = workflow.artifacts.some(
    (artifact) => artifact.type === 'gitopsDeployment'
  );

  if (!isReconciled) {
    return null;
  }

  return (
    <LoadingButton
      color="light"
      size={size}
      icon={RefreshCw}
      isLoading={mutation.isLoading}
      loadingText="Requesting..."
      data-cy={`workflow-reconcile-button-${workflow.id}`}
      onClick={handleClick}
    >
      Reconcile
    </LoadingButton>
  );

  function handleClick() {
    mutation.mutate(workflow.id, {
      onSuccess() {
        notifySuccess(
          'Reconcile requested',
          'Each environment will apply on its next check-in.'
        );
      },
    });
  }
}
